import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  buildLetterTemplatePreviewDocument,
  getLetterTemplateById,
  LETTER_TEMPLATE_NAME_MAX_LENGTH,
  updateLetterTemplate,
} from '../../services/letterTemplateMasters';
import { setFlashToast } from '../../utils/flashToast';
import { formatDocumentTableDateTime } from '../../utils/dateFormat';
import {
  focusFirstInvalid,
  validateRequired,
  type RequiredErrors,
  type RequiredFieldSpec,
} from '../../utils/requiredValidation';
import { exceedsMax, maxCharLimitExceededError } from '../../utils/caseFieldValidation';

const READONLY_FIELD_STYLE = {
  background: 'color-mix(in srgb, var(--bg) 70%, var(--surface))',
  color: 'var(--t2)',
  cursor: 'not-allowed' as const,
};

function base64ToPdfUrl(base64: string): string | null {
  const trimmed = base64.trim();
  if (!trimmed) return null;
  try {
    const binary = atob(trimmed.replace(/^data:application\/pdf;base64,/, ''));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

export default function LetterTemplateFormPage() {
  const { t } = useTranslation();
  const { letterTemplateId } = useParams();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});

  const [templateName, setTemplateName] = useState('');
  const [active, setActive] = useState(true);
  const [updatedAt, setUpdatedAt] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [subCategoryName, setSubCategoryName] = useState('');

  const [letterContent, setLetterContent] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!letterTemplateId) return;
    let alive = true;
    setLoading(true);
    void getLetterTemplateById(letterTemplateId).then((res) => {
      if (!alive) return;
      if (res.success) {
        const d = res.data;
        setTemplateName(d.templateName);
        setActive(d.active);
        setUpdatedAt(d.updatedAt);
        setUpdatedBy(d.updatedBy);
        setCategoryName(d.categoryName);
        setSubCategoryName(d.subCategoryName);
        setPdfUrl(d.pdfUrl);
        setLetterContent(d.letterContent);
        setPreviewBlobUrl(d.pdfBase64 ? base64ToPdfUrl(d.pdfBase64) : null);
      } else {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [letterTemplateId, t]);

  useEffect(() => {
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    };
  }, [previewBlobUrl]);

  const requiredSpecs = useMemo<RequiredFieldSpec[]>(() => {
    return [
      {
        key: 'templateName',
        label: t('metadata.letterTemplates.templateName'),
        value: templateName,
        elementId: 'letter-template-name',
      },
    ];
  }, [templateName, t]);

  const previewSrc = previewBlobUrl || pdfUrl || '';
  const previewDocument = useMemo(
    () => buildLetterTemplatePreviewDocument(letterContent),
    [letterContent],
  );

  const displayOrDash = (value: string) => value.trim() || '\u2014';

  async function handleSave() {
    if (!letterTemplateId || savingRef.current) return;

    const errs = validateRequired(requiredSpecs);
    if (exceedsMax(templateName, LETTER_TEMPLATE_NAME_MAX_LENGTH)) {
      errs.templateName = maxCharLimitExceededError();
    }
    setReqErrors(errs);
    if (Object.keys(errs).length) {
      focusFirstInvalid(requiredSpecs, errs);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    let keepLocked = false;
    try {
      const res = await updateLetterTemplate(letterTemplateId, {
        templateName: templateName.trim(),
        active,
      });
      if (!res.success) {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
        return;
      }
      setFlashToast({
        message: t('metadata.letterTemplateForm.updateSuccess'),
        variant: 'success',
      });
      nav('/metadata/letter-templates');
      keepLocked = true;
    } finally {
      if (!keepLocked) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        <Link to="/metadata/letter-templates">{t('metadata.letterTemplates.title')}</Link>
        <span className="bc-sep">/</span>
        {t('metadata.letterTemplateForm.updateTitle')}
      </div>

      <div className="pg-title">{t('metadata.letterTemplateForm.updateTitle')}</div>

      <div className="cr-table-wrap" style={{ overflow: 'visible' }}>
        <div style={{ padding: 18 }}>
          {loading ? <div style={{ color: 'var(--t2)' }}>{t('common.loading')}</div> : null}
          {reqErrors.form ? <div className="si-err" style={{ marginBottom: 12 }}>{reqErrors.form}</div> : null}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="al-f">
              <div className="al-l">{t('admin.shared.fileCategory')}</div>
              <input className="al-inp" value={displayOrDash(categoryName)} readOnly style={READONLY_FIELD_STYLE} />
            </div>
            <div className="al-f">
              <div className="al-l">{t('admin.shared.subFileCategory')}</div>
              <input className="al-inp" value={displayOrDash(subCategoryName)} readOnly style={READONLY_FIELD_STYLE} />
            </div>
            <div className="al-f">
              <div className="al-l">
                {t('metadata.letterTemplates.templateName')} <span className="req">*</span>
              </div>
              <input
                id="letter-template-name"
                className="al-inp"
                value={templateName}
                maxLength={LETTER_TEMPLATE_NAME_MAX_LENGTH}
                placeholder={t('metadata.letterTemplateForm.namePlaceholder')}
                onChange={(e) => {
                  setTemplateName(e.target.value);
                  if (reqErrors.templateName) {
                    setReqErrors((cur) => {
                      const next = { ...cur };
                      delete next.templateName;
                      return next;
                    });
                  }
                }}
              />
              {reqErrors.templateName ? <div className="si-err">{reqErrors.templateName}</div> : null}
            </div>
            <div className="al-f letter-template-active-cell">
              <div className="al-l" aria-hidden="true">
                &nbsp;
              </div>
              <label className="ccr-chk letter-template-active-chk">
                <input
                  type="checkbox"
                  checked={active}
                  disabled={loading}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>{t('common.active')}</span>
              </label>
            </div>
            <div className="al-f">
              <div className="al-l">{t('metadata.letterTemplates.lastUpdatedAt')}</div>
              <input
                className="al-inp"
                value={formatDocumentTableDateTime(updatedAt) || '\u2014'}
                readOnly
                style={READONLY_FIELD_STYLE}
              />
            </div>
            <div className="al-f">
              <div className="al-l">{t('metadata.letterTemplates.lastUpdatedBy')}</div>
              <input
                className="al-inp"
                value={displayOrDash(updatedBy)}
                readOnly
                style={READONLY_FIELD_STYLE}
              />
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <div className="al-l" style={{ marginBottom: 8 }}>
              {t('metadata.letterTemplateForm.viewTemplate')}
            </div>
            {previewSrc ? (
              <iframe
                title={t('metadata.letterTemplateForm.viewTemplate')}
                src={previewSrc}
                style={{
                  width: '100%',
                  minHeight: 520,
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r)',
                  background: 'var(--surface)',
                }}
              />
            ) : previewDocument ? (
              <iframe
                title={t('metadata.letterTemplateForm.viewTemplate')}
                srcDoc={previewDocument}
                style={{
                  width: '100%',
                  minHeight: 520,
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r)',
                  background: '#fff',
                }}
              />
            ) : (
              <div className="si-err" style={{ marginTop: 0 }}>
                {t('metadata.letterTemplateForm.previewUnavailable')}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <Button variant="primary" disabled={loading || saving} onClick={() => void handleSave()}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
            <Button disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('common.cancel')}
        message={t('metadata.letterTemplateForm.cancelConfirm')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => nav('/metadata/letter-templates')}
      />
    </>
  );
}
